import { useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { ITEM_CATEGORIES, PhotoUpload, Modal, inp, lbl } from "./GearBits";

export default function ItemForm({ item, bags, travelers, trips, onSaved, onClose, defaults = {} }) {
  const [f, setF] = useState({
    name: "", category: "clothing", quantity: 1, owner_id: "", owner_name: "", bag_id: "", trip_id: "",
    brand: "", model: "", serial_number: "", carry_on_only: false, restricted: false, high_value: false,
    value: "", photo_url: null, notes: "", ...defaults, ...(item || {}),
  });
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e });

  const save = async () => {
    if (!f.name.trim()) return toast.error("Item name is required");
    setBusy(true);
    try {
      const owner = travelers.find((t) => t.id === f.owner_id);
      const payload = { ...f, owner_name: f.owner_id === "shared" ? "Shared" : owner?.name || f.owner_name || null,
        owner_id: f.owner_id || null, bag_id: f.bag_id || null, trip_id: f.trip_id || null };
      if (item?.id) await api.put(`/gear/items/${item.id}`, payload);
      else await api.post("/gear/items", payload);
      toast.success(item?.id ? "Item updated" : "Item added");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save item");
    } finally { setBusy(false); }
  };

  return (
    <Modal title={item?.id ? "Edit item" : "Add an item"} onClose={onClose}>
      <div className="space-y-3" data-testid="item-form">
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><label className={lbl}>Item name *</label>
            <input value={f.name} onChange={set("name")} data-testid="item-name" className={inp} placeholder="Phone charger, neck pillow, kid's favorite toy…" /></div>
          <div><label className={lbl}>Category</label>
            <select value={f.category} onChange={set("category")} data-testid="item-category" className={inp}>
              {ITEM_CATEGORIES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></div>
          <div><label className={lbl}>Quantity</label>
            <input type="number" min="1" value={f.quantity} onChange={set("quantity")} data-testid="item-qty" className={inp} /></div>
          <div><label className={lbl}>Owner</label>
            <select value={f.owner_id || ""} onChange={set("owner_id")} data-testid="item-owner" className={inp}>
              <option value="">— No owner set —</option>
              <option value="shared">Shared (family / group)</option>
              {travelers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select></div>
          <div><label className={lbl}>In which bag?</label>
            <select value={f.bag_id || ""} onChange={set("bag_id")} data-testid="item-bag" className={inp}>
              <option value="">— Not in a bag —</option>
              {bags.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select></div>
        </div>

        <div className="flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!f.carry_on_only} onChange={set("carry_on_only")} data-testid="item-carryon" className="w-4 h-4 accent-primary" /> Carry-on only (never check)</label>
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!f.restricted} onChange={set("restricted")} data-testid="item-restricted" className="w-4 h-4 accent-primary" /> Security / liquid restricted</label>
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!f.high_value} onChange={set("high_value")} data-testid="item-highvalue" className="w-4 h-4 accent-primary" /> High value</label>
        </div>

        <button type="button" onClick={() => setMore(!more)} className="text-xs text-primary hover:underline" data-testid="item-more-toggle">
          {more ? "Hide optional details" : "More details (photo, serial number, trip…) — optional"}
        </button>
        {more && (
          <div className="space-y-3 border border-zinc-200 p-3 bg-zinc-50/50">
            <PhotoUpload value={f.photo_url} onChange={(u) => setF({ ...f, photo_url: u })} testid="item-photo-upload" />
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className={lbl}>Brand / model</label>
                <div className="flex gap-2">
                  <input value={f.brand || ""} onChange={set("brand")} className={inp} placeholder="Brand" />
                  <input value={f.model || ""} onChange={set("model")} className={inp} placeholder="Model" />
                </div></div>
              <div><label className={lbl}>Serial / IMEI (electronics)</label><input value={f.serial_number || ""} onChange={set("serial_number")} data-testid="item-serial" className={inp} /></div>
              <div><label className={lbl}>Value</label><input value={f.value || ""} onChange={set("value")} className={inp} placeholder="$120" /></div>
              <div><label className={lbl}>Trip</label>
                <select value={f.trip_id || ""} onChange={set("trip_id")} className={inp}>
                  <option value="">— Not on a trip —</option>
                  {trips.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
                </select></div>
            </div>
            <div><label className={lbl}>Notes</label><textarea value={f.notes || ""} onChange={set("notes")} rows={2} className={inp} /></div>
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-zinc-300">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="item-save" className="px-5 py-2 text-sm font-bold bg-primary text-white hover:bg-blue-700 disabled:opacity-50">
            {busy ? "Saving…" : item?.id ? "Save changes" : "Add item"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
